import { Extension } from "@tiptap/core";
import { TextStyle } from "@tiptap/extension-text-style";

/**
 * Text colours, highlights and badges for the rich text editor.
 *
 * All three are attributes on one `textStyle` span, rendered as inline styles
 * with fixed colour values, so the HTML looks the same in an email client as
 * in the editor. Each also writes a `data-*` name so it round-trips by name.
 * A badge sets its own background and text colour, so it replaces any text
 * colour or highlight on the same text, and the reverse.
 *
 * Highlights and subtle badges use translucent backgrounds so they read on
 * both a light and a dark page.
 */

export const TEXT_COLOR_NAMES = ["red", "orange", "yellow", "blue", "purple", "pink", "green", "grey"] as const;

export type TextColorName = (typeof TEXT_COLOR_NAMES)[number];

export type BadgeVariant = "solid" | "subtle";

interface Swatch {
  label: string;
  /** Text colour, and the text of a subtle badge. */
  text: string;
  /** Highlight, and the background of a subtle badge. */
  tint: string;
  /** Background of a solid badge. */
  solid: string;
  /** Text on a solid badge. */
  onSolid: string;
}

export const TEXT_COLORS: Record<TextColorName, Swatch> = {
  red: { label: "Red", text: "#e5484d", tint: "rgba(229, 72, 77, 0.24)", solid: "#dc2626", onSolid: "#ffffff" },
  orange: { label: "Orange", text: "#f76b15", tint: "rgba(247, 107, 21, 0.24)", solid: "#ea580c", onSolid: "#ffffff" },
  yellow: { label: "Yellow", text: "#d99a00", tint: "rgba(234, 179, 8, 0.28)", solid: "#facc15", onSolid: "#1c1917" },
  blue: { label: "Blue", text: "#3b82f6", tint: "rgba(59, 130, 246, 0.24)", solid: "#2563eb", onSolid: "#ffffff" },
  purple: { label: "Purple", text: "#8b5cf6", tint: "rgba(139, 92, 246, 0.26)", solid: "#7c3aed", onSolid: "#ffffff" },
  pink: { label: "Pink", text: "#e8458b", tint: "rgba(232, 69, 139, 0.24)", solid: "#db2777", onSolid: "#ffffff" },
  green: { label: "Green", text: "#22a55b", tint: "rgba(34, 165, 91, 0.24)", solid: "#16a34a", onSolid: "#ffffff" },
  grey: { label: "Grey", text: "#8a8f98", tint: "rgba(138, 143, 152, 0.26)", solid: "#6b7280", onSolid: "#ffffff" },
};

const swatch = (name: unknown): Swatch | undefined =>
  typeof name === "string" && name in TEXT_COLORS ? TEXT_COLORS[name as TextColorName] : undefined;

/** The CSS colour for a stored value: a palette name, or a raw colour that came in on pasted HTML. */
const resolve = (value: unknown, pick: (entry: Swatch) => string): string | null =>
  typeof value === "string" && value ? (swatch(value) ? pick(swatch(value) as Swatch) : value) : null;

// No font-weight: StarterKit's Bold reads font-weight >= 500 as <strong> on the next load.
export const badgeStyle = (name: TextColorName, variant: BadgeVariant): string => {
  const entry = TEXT_COLORS[name];
  const [background, color] = variant === "solid" ? [entry.solid, entry.onSolid] : [entry.tint, entry.text];
  return `background-color: ${background}; color: ${color}; border-radius: 4px; padding: 0 4px`;
};

const isBadge = (element: HTMLElement) => element.hasAttribute("data-badge");

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textColors: {
      /** Colours the selected text: a palette name, or null to clear it. Clears any badge. */
      setTextColor: (color: TextColorName | null) => ReturnType;
      /** Highlights the selected text: a palette name, or null to clear it. Clears any badge. */
      setTextHighlight: (color: TextColorName | null) => ReturnType;
      /** Turns the selected text into a badge, or null to clear it. Clears any text colour and highlight. */
      setTextBadge: (color: TextColorName | null, variant?: BadgeVariant) => ReturnType;
      /** Clears text colour, highlight and badge. */
      unsetTextColors: () => ReturnType;
    };
  }
}

export interface TextColorsOptions {
  /** Mark types that carry the attributes. Default `["textStyle"]`. */
  types: string[];
}

export const TextColors = Extension.create<TextColorsOptions>({
  name: "textColors",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addExtensions() {
    // Nested-span merging would copy a badge's style onto an attribute chip inside it.
    return [TextStyle.configure({ mergeNestedSpanStyles: false })];
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          color: {
            default: null,
            parseHTML: (element) =>
              isBadge(element) ? null : element.getAttribute("data-color") || element.style.color || null,
            renderHTML: (attributes) => {
              const color = attributes.badge ? null : resolve(attributes.color, (entry) => entry.text);
              if (!color) return {};
              return {
                style: `color: ${color}`,
                ...(swatch(attributes.color) ? { "data-color": attributes.color } : {}),
              };
            },
          },
          highlight: {
            default: null,
            parseHTML: (element) =>
              isBadge(element) ? null : element.getAttribute("data-highlight") || element.style.backgroundColor || null,
            renderHTML: (attributes) => {
              const color = attributes.badge ? null : resolve(attributes.highlight, (entry) => entry.tint);
              if (!color) return {};
              return {
                style: `background-color: ${color}`,
                ...(swatch(attributes.highlight) ? { "data-highlight": attributes.highlight } : {}),
              };
            },
          },
          badge: {
            default: null,
            parseHTML: (element) =>
              swatch(element.getAttribute("data-badge")) ? element.getAttribute("data-badge") : null,
            renderHTML: (attributes) => {
              if (!swatch(attributes.badge)) return {};
              const variant: BadgeVariant = attributes.badgeVariant === "subtle" ? "subtle" : "solid";
              return { "data-badge": attributes.badge, style: badgeStyle(attributes.badge, variant) };
            },
          },
          badgeVariant: {
            default: null,
            parseHTML: (element) => (isBadge(element) ? element.getAttribute("data-badge-variant") : null),
            renderHTML: (attributes) =>
              attributes.badge && attributes.badgeVariant === "subtle" ? { "data-badge-variant": "subtle" } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextColor:
        (color) =>
        ({ chain }) =>
          chain().setMark("textStyle", { color, badge: null, badgeVariant: null }).removeEmptyTextStyle().run(),
      setTextHighlight:
        (color) =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { highlight: color, badge: null, badgeVariant: null })
            .removeEmptyTextStyle()
            .run(),
      setTextBadge:
        (color, variant = "solid") =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", {
              badge: color,
              badgeVariant: color && variant === "subtle" ? "subtle" : null,
              color: null,
              highlight: null,
            })
            .removeEmptyTextStyle()
            .run(),
      unsetTextColors:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { color: null, highlight: null, badge: null, badgeVariant: null })
            .removeEmptyTextStyle()
            .run(),
    };
  },
});
